import { AUTH_PRODUCT_NAME } from "./authProduct";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "../../contexts/LanguageContext";
import { AuthAlert } from "./AuthPrimitives";
import { AuthLayout } from "./AuthLayout";
import { ConfirmEmailForm } from "./ConfirmEmailForm";
import { ForgotPasswordForm } from "./ForgotPasswordForm";
import { LoginForm } from "./LoginForm";
import { RegisterForm } from "./RegisterForm";
import { ResetPasswordForm } from "./ResetPasswordForm";

type AuthView =
  | "login"
  | "register"
  | "forgot-password"
  | "reset-password"
  | "confirm-email";

/** Uma URL por tela; a tela é sempre derivada do caminho. */
export const AUTH_VIEW_PATHS: Record<AuthView, string> = {
  login: "/auth",
  register: "/auth/register",
  "forgot-password": "/auth/forgot-password",
  "reset-password": "/auth/reset-password",
  "confirm-email": "/auth/confirm-email",
};

const viewFromPath = (pathname: string): AuthView => {
  const path = pathname.replace(/\/$/, "");
  const match = (Object.keys(AUTH_VIEW_PATHS) as AuthView[]).find(
    (view) => AUTH_VIEW_PATHS[view] === path,
  );
  return match ?? "login";
};

// Parâmetros que acompanham a troca de tela (login pedido por outro produto Virtus)
const CARRIED_PARAMS = ["redirect_uri", "sso"];

export const AuthPage: React.FC = () => {
  const { t } = useTranslation();
  const [message, setMessage] = useState<{
    text: string;
    type: "success" | "error" | "warning";
  } | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const currentView = viewFromPath(location.pathname);

  const setCurrentView = (view: AuthView) => {
    const current = new URLSearchParams(location.search);
    const next = new URLSearchParams();
    CARRIED_PARAMS.forEach((key) => {
      const value = current.get(key);
      if (value) next.set(key, value);
    });
    const search = next.toString();
    navigate(`${AUTH_VIEW_PATHS[view]}${search ? `?${search}` : ""}`);
  };

  // Atualizar título do documento baseado na view atual
  useEffect(() => {
    const titles: Record<AuthView, string> = {
      login: t("Entrar"),
      register: t("Criar conta"),
      "forgot-password": t("Recuperar Senha"),
      "reset-password": t("Redefinir Senha"),
      "confirm-email": t("Confirmar Email"),
    };

    const pageTitle = titles[currentView] || t("Autenticação");
    document.title = `${pageTitle} | Virtus ${AUTH_PRODUCT_NAME}`;
  }, [currentView, t]);

  // Capturar mensagens do estado de navegação
  useEffect(() => {
    if (location.state?.message) {
      setMessage({
        text: location.state.message,
        type: location.state.type || "success",
      });

      // Limpar a mensagem após 5 segundos
      const timer = setTimeout(() => {
        setMessage(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // Exibir aviso persistido em redirects forçados (ex.: sessão expirada).
  useEffect(() => {
    const redirectMessage = sessionStorage.getItem("auth_redirect_message");
    if (redirectMessage) {
      setMessage({ text: redirectMessage, type: "warning" });
      sessionStorage.removeItem("auth_redirect_message");
    }
  }, []);

  const handleForgotPassword = () => {
    setCurrentView("forgot-password");
  };

  return (
    <AuthLayout>
      <div className="space-y-5">
        {message && (
          <AuthAlert
            variant={message.type}
            title={
              message.type === "warning" ? t("Sessão expirada") : undefined
            }
            message={message.text}
          />
        )}

        <div key={currentView} className="animate-fade-in">
          {currentView === "login" && (
            <LoginForm
              onSwitchToRegister={() => setCurrentView("register")}
              onForgotPassword={handleForgotPassword}
            />
          )}

          {currentView === "register" && (
            <RegisterForm onSwitchToLogin={() => setCurrentView("login")} />
          )}

          {currentView === "forgot-password" && (
            <ForgotPasswordForm
              onBackToLogin={() => setCurrentView("login")}
              onGoToResetPassword={() => setCurrentView("reset-password")}
            />
          )}

          {currentView === "reset-password" && <ResetPasswordForm />}

          {currentView === "confirm-email" && <ConfirmEmailForm />}
        </div>
      </div>
    </AuthLayout>
  );
};
